import { InfoCircleFilled, UserAddOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Input,
  Modal,
  Row,
  Typography,
  Space,
  Select,
  InputNumber,
  Tooltip,
} from "antd"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore"
import React, { useEffect, useState } from "react"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"
import { useCollection } from "../../hooks/useCollection"
import { Notification } from "../Notification"
import { CountryCodesSelect } from "./CountryCodesSelect"
import { checkVatExists, normalizeVat } from "../../utils/VatChecker"

const { Text } = Typography
const { Option } = Select
const initVal = null
export default function CreateClientPopup({ icon, prefill = null }) {
  //user from auth context
  const { user } = useAuthContext()
  //gets client group data
  const { data } = useCollection("clientGroups", ["user", "==", user.uid], ["approved", "==", true])

  //sets state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [client, setClient] = useState("")
  const [vat, setVat] = useState("")
  const [reg, setReg] = useState("")
  const [accountant, setAccountant] = useState(initVal)
  const [accountantEmail, setAccountantEmail] = useState(initVal)
  const [accountantNumber, setAccountantNumber] = useState(initVal)
  const [contactEmail, setContactEmail] = useState(initVal)
  const [contactName, setContactName] = useState(initVal)
  const [contactNumber, setContactNumber] = useState(initVal)
  const [deliveryAddress, setDeliveryAddress] = useState(initVal)
  const [deliveryAddress2, setDeliveryAddress2] = useState(initVal)
  const [city, setCity] = useState(initVal)
  const [street, setStreet] = useState(initVal)
  const [postalCode, setPostalCode] = useState(initVal)

  //verification
  const [vatNotVerified, setVatNotVerified] = useState(false)
  const [regNotVerified, setRegNotVerified] = useState(false)
  const [companyNameNotVerified, setCompanyNameNotVerified] = useState(false)
  const [loadButton, setLoadButton] = useState(false)

  //specific for mp vertification
  const [mpVerified, setMpVerified] = useState(false)

  // country state
  const [selectedCountry, setSelectedCountry] = useState("Latvia")
  const [selectedCountryCode, setSelectedCountryCode] = useState("LV")
  const [selectedPhoneEx, setSelectedPhoneEx] = useState("+371")

  // client group
  const [hasClientGroup, setHasClientGroup] = useState(false)
  const [selectedClientGroup, setSelectedClientGroup] = useState(null)
  const [clientGroupProfit, setClientGroupProfit] = useState(0)

  //prefill for lead convert
  useEffect(() => {
    if (prefill) {
      setClient(prefill.client || "")
      setVat(prefill.vat?.replace(/^[A-Z]{2}/, "") || "")
      setSelectedCountry(prefill.country || "Latvia")
      setSelectedCountryCode(prefill.vat?.slice(0, 2) || "LV")
    }
  }, [prefill])

  //creates client
  const createClient = async (e) => {
    e.preventDefault()

    //sets button loading
    setLoadButton(true)

    //checks if fields are empty
    //Company Name
    if (client.trim().length === 0) {
      setCompanyNameNotVerified(true)
    }

    //vat
    if (vat.trim().length === 0) {
      setVatNotVerified(true)
    }

    //reg
    if (reg.trim().length === 0) {
      setRegNotVerified(true)
    }

    // //mp specific
    if (user.uid === "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2") {
      if (clientGroupProfit <= 0) {
        setMpVerified(true)
      }
    }

    //executes update
    if (reg.trim().length > 0 && vat.trim().length > 0 && client.trim().length > 0) {
      if (user.uid === "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2") {
        if (clientGroupProfit > 0) {
          addData()
        }
      } else {
        addData()
      }
    }

    setLoadButton(false)
  }

  // function that adds data to db
  const addData = async () => {
    try {
      // 1) Normalize VAT and check duplicates in clients + leads
      const vatNormalized = normalizeVat(selectedCountryCode, vat)
      const dup = await checkVatExists(vatNormalized, prefill?.id)

      if (dup.exists) {
        Notification(
          "error",
          "Duplicate VAT",
          `This VAT already exists in ${dup.where} as "${dup.name || dup.id}".`
        )
        return
      }

      // 2) Create the new client
      const clientsCol = collection(db, "clients")
      const newClientRef = await addDoc(clientsCol, {
        client: client,
        vat: vatNormalized,
        reg: reg,
        country: selectedCountry,

        accountant: accountant,
        accountantEmail: accountantEmail,
        accountantNumber: accountantNumber,

        contactEmail: contactEmail,
        contactName: contactName,
        contactNumber: selectedPhoneEx + contactNumber,

        deliveryAddress: deliveryAddress,
        deliveryAddress2: deliveryAddress2,
        city: city,
        street: street,
        postalCode: postalCode,

        user: user.uid,
        userEmail: user.email,

        approved: false, // new clients start unapproved
        orderCredit: 0,
        creditLimit: 9000,
        creditUsed: 0,
        creditLeft: 9000,
        tapeClient: false,

        profitability: clientGroupProfit,
        clientGroup: selectedClientGroup,

        accountNumber: null,
        bankCode: null,
        bankName: null,

        date: {
          timestamp: serverTimestamp(),
          createdAt: serverTimestamp(),
          lastEdit: serverTimestamp(),
        },
        status: {
          clientStatus: 0,
          created: serverTimestamp(),
          approved: null,
          ordered: null,
        },
      })

      const newClientId = newClientRef.id

      // 3) If we are converting from a lead:
      //    - delete the lead
      //    - update related priceCalculation and priceTemplates docs to point to the new client id
      if (prefill?.id) {
        // delete the old lead
        await deleteDoc(doc(db, "leads", prefill.id))

        // move priceCalculation references
        const priceCalcCol = collection(db, "priceCalculation")
        const calcQuery = query(priceCalcCol, where("clientId", "==", prefill.id))
        const calcSnap = await getDocs(calcQuery)
        for (const d of calcSnap.docs) {
          await updateDoc(d.ref, {
            clientId: newClientId,
            client: client,
            approved: false,
            clientisLead: false,
          })
        }

        // move priceTemplates references
        const templatesCol = collection(db, "priceTemplates")
        const tplQuery = query(templatesCol, where("clientId", "==", prefill.id))
        const tplSnap = await getDocs(tplQuery)
        for (const d of tplSnap.docs) {
          await updateDoc(d.ref, {
            clientId: newClientId,
            client: client,
            clientisLead: false,
          })
        }
      }

      // 4) Done
      Notification("success", "Client Added", "You have successfully added a new client!")
      onClose()
      setIsModalOpen(false)
    } catch (err) {
      console.error("Error creating client:", err)
      Notification("error", "Creation Error", err.message)
    }
  }

  //resets values on close
  const onClose = () => {
    setVatNotVerified(false)
    setRegNotVerified(false)
    setCompanyNameNotVerified(false)
    setMpVerified(false)
    setClient("")
    setVat("")
    setReg("")
    setAccountant(initVal)
    setAccountantEmail(initVal)
    setAccountantNumber(initVal)
    setContactEmail(initVal)
    setContactName(initVal)
    setContactNumber(initVal)
    setDeliveryAddress(initVal)
    setDeliveryAddress2(initVal)
    setSelectedPhoneEx("+371")
    setSelectedCountryCode("LV")
    setSelectedCountry("Latvia")
    setCity(initVal)
    setStreet(initVal)
    setPostalCode(initVal)
    setIsModalOpen(false)
    setClientGroupProfit(0)
  }

  //checks if there is a need for client group
  const handleClientGroup = (value) => {
    setHasClientGroup(value)
    setSelectedClientGroup(null)
    setClientGroupProfit(0)
  }
  //handles onChange for client group
  const handleClientGroupData = (value, option) => {
    setSelectedClientGroup(value)
    setClientGroupProfit(option.profitability)
    console.log(value)
  }
  const showModal = () => {
    setIsModalOpen(true)
  }

  const handleOk = (e) => {
    createClient(e)
  }

  const handleCancel = () => {
    onClose()
  }

  //text for mpdruka tooltip
  const text = (
    <>
      <span>
        This is a client percent. If your client has no group, you can input this percent, otherwise
        upon selecting your group, the percent will show up!
      </span>
    </>
  )

  const handleClientChange = (e) => {
    setClient(e.target.value)
    if (e.target.value.trim().length > 0) {
      setCompanyNameNotVerified(false)
    }
  }

  const handleRegChange = (e) => {
    setReg(e.target.value)
    setRegNotVerified(false)
    if (e.target.value.trim().length > 0) {
      setRegNotVerified(false)
    }
  }

  const handleVatChange = (e) => {
    setVat(e.target.value)
    if (e.target.value.trim().length > 0) {
      setVatNotVerified(false)
    }
  }

  const handlePercentUpdate = (value) => {
    setClientGroupProfit(value)
    if (value > 0) {
      setMpVerified(false)
    }
  }

  const returnButtonOrIcon = () => {
    if (icon) {
      return (
        <>
          <Button
            onClick={showModal}
            type="primary"
            icon={<UserAddOutlined style={{ color: "white" }} />}></Button>
        </>
      )
    } else {
      return (
        <Button type="primary" onClick={showModal}>
          Create Client
        </Button>
      )
    }
  }

  return (
    <>
      {returnButtonOrIcon()}

      <Modal
        title="Create Client"
        open={isModalOpen}
        onOk={handleOk}
        okButtonProps={{ loading: loadButton }}
        okText="Create Client"
        onCancel={handleCancel}
        width={1000}>
        <Space direction="vertical">
          <Row gutter={[16, 10]}>
            <Col span={8}>
              <Text>Has Client Group?</Text>
              <Input.Group compact>
                <Select onChange={handleClientGroup} style={{ width: "25%" }} defaultValue="No">
                  <Option value={false}>No</Option>
                  <Option value={true}>Yes</Option>
                </Select>
                <Select
                  showSearch
                  disabled={hasClientGroup ? false : true}
                  style={{
                    width: "75%",
                  }}
                  onChange={handleClientGroupData}
                  placeholder={hasClientGroup ? "Orkla Latvia" : "Setting Disabled"}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  }>
                  {data &&
                    data.map((group, id) => (
                      <Option
                        id={group.id}
                        profitability={group.profitability}
                        key={id}
                        value={group.groupName}>
                        {group.groupName}
                      </Option>
                    ))}
                </Select>
              </Input.Group>
            </Col>
            <Col span={8}>
              <Space>
                <Text>Company Name</Text>
                <Text italic style={{ color: "red", fontSize: "10px" }}>
                  (required)
                </Text>
              </Space>

              <Input
                status={companyNameNotVerified ? "error" : ""}
                value={client}
                onChange={(e) => handleClientChange(e)}
                placeholder="Staburadze"
              />
            </Col>

            <Col span={8}>
              <Text>Client's Country</Text>
              <CountryCodesSelect
                defaultCountry={selectedCountry}
                setSelectedPhoneEx={setSelectedPhoneEx}
                setSelectedCountryCode={setSelectedCountryCode}
                setSelectedCountry={setSelectedCountry}
              />
            </Col>
            <Col span={8}>
              <Space>
                <Text>Reg. Number</Text>
                <Text italic style={{ color: "red", fontSize: "10px" }}>
                  (required)
                </Text>
              </Space>

              <Input
                status={regNotVerified ? "error" : ""}
                value={reg}
                onChange={(e) => handleRegChange(e)}
                placeholder="40103217882"
              />
            </Col>
            <Col span={8}>
              <Space size="small">
                <Text>VAT Number</Text>
                <Text italic style={{ color: "red", fontSize: "10px" }}>
                  (required)
                </Text>
              </Space>
              <Input
                status={vatNotVerified ? "error" : ""}
                prefix={selectedCountryCode}
                value={vat}
                onChange={(e) => handleVatChange(e)}
                placeholder="40103217882"
              />
            </Col>

            <Col span={8}>
              <Text>Contact Email</Text>
              <Input
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="anna@staburadze.lv"
              />
            </Col>
            <Col span={8}>
              <Text>Contact Number</Text>
              <Input.Group compact>
                <Input
                  style={{
                    width: "25%",
                  }}
                  defaultValue="+371"
                  value={selectedPhoneEx}
                  onChange={(e) => setSelectedPhoneEx(e.target.value)}
                />
                <Input
                  style={{ width: "75%" }}
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  placeholder="67080302"
                />
              </Input.Group>
            </Col>
            <Col span={8}>
              <Text>Contact Name</Text>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Anna Staburadze"
              />
            </Col>
          </Row>

          <Row gutter={[16, 10]}>
            <Col span={8}>
              <Text>City</Text>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Riga" />
            </Col>
            <Col span={8}>
              <Text>Street Address</Text>
              <Input
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Miera street 22"
              />
            </Col>
            <Col span={8}>
              <Text>Postal Code</Text>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="LV-1001"
              />
            </Col>
          </Row>
          {user && user.uid === "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2" ? (
            <Row gutter={[16, 10]}>
              <Col span={8}>
                <Space>
                  <Tooltip placement="bottom" title={text}>
                    <InfoCircleFilled />
                  </Tooltip>
                  <Text>Client's profit</Text>
                  <Text italic style={{ color: "red", fontSize: "10px" }}>
                    (required)
                  </Text>
                </Space>

                <InputNumber
                  min={0}
                  max={100}
                  status={mpVerified ? "error" : ""}
                  formatter={(value) => `${value}%`}
                  value={clientGroupProfit}
                  style={{ width: "100%" }}
                  onChange={handlePercentUpdate}
                />
              </Col>
            </Row>
          ) : (
            ""
          )}
          <Row justify="center" gutter={[16, 10]}>
            <Col>
              {vatNotVerified || regNotVerified || companyNameNotVerified ? (
                <Text italic style={{ color: "red" }}>
                  Please fill out the required fields!
                </Text>
              ) : (
                ""
              )}
            </Col>
          </Row>
        </Space>
      </Modal>
    </>
  )
}
